import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AccountShell,
  BackLink,
  PageHeader,
  mono,
  serif,
} from "@/components/AccountLayout";
import { getMyAccount, type MyAccountResult } from "@/lib/account.functions";

export const Route = createFileRoute("/account_/details")({
  head: () => {
    const title = "Your details — WHO KNEW";
    const description =
      "Manage your WHO KNEW account, view your purchase, track your progress, or get in touch.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/account/details" },
      ],
    };
  },
  component: DetailsPage,
});

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p
        className="font-bold uppercase tracking-[0.05em]"
        style={{ ...mono, fontSize: "10px", color: "#5A534A" }}
      >
        {label}
      </p>
      <p className="text-ink" style={{ ...serif, fontSize: "18px", marginTop: "4px" }}>
        {value}
      </p>
    </div>
  );
}

function formatMonthYear(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

function DetailsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<MyAccountResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyAccount()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok && res.error === "no_session") {
          navigate({ to: "/" });
          return;
        }
        setState(res);
      })
      .catch(() => {
        if (!cancelled) setState({ ok: false, error: "server" });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <AccountShell>
      <BackLink />
      <PageHeader eyebrow="YOUR DETAILS" title="Your details." />

      <div style={{ marginTop: "48px" }}>
        {state?.ok ? (
          <>
            <Row label="EMAIL" value={state.email} />
            <div style={{ height: "12px" }} />
            <Row label="MEMBER SINCE" value={formatMonthYear(state.memberSince)} />
          </>
        ) : (
          <p
            className="italic"
            style={{ ...serif, fontSize: "16px", color: "#5A534A" }}
          >
            Loading…
          </p>
        )}
      </div>

      <p
        className="italic"
        style={{
          ...serif,
          fontSize: "14px",
          color: "#5A534A",
          marginTop: "24px",
          paddingBottom: "96px",
        }}
      >
        Email changes and other niceties land with the next release.
      </p>
    </AccountShell>
  );
}
